import { pb } from "../pocketbase/pocketbase";
import type {
  DeletedRecord,
  HistoryCollection,
  HistoryTimeline,
  HistoryVersionDetail,
  RestoreResult,
} from "../../core/history/entities/HistoryVersion";

/// `dataHistory` n'a aucune règle d'accès (superuser only) : la table n'est donc pas interrogeable
/// via l'API collections. Tout passe par les routes `/api/history/*` du backend, qui vérifient le
/// rôle de l'admin sur la formation de la cible.
///
/// N'implémente pas `RepositoryInterface` (comme `adminTrainingRepository`) : ce n'est pas un CRUD.

interface BackendResponse {
  isSuccess: boolean;
  code?: string;
  message?: string;
}

/// Les routes renvoient un statut 400 avec un corps `{ isSuccess: false, code }` : le SDK lève
/// alors une `ClientResponseError`. On normalise en `Error` porteuse du code métier.
function assertSuccess(response: BackendResponse) {
  if (!response?.isSuccess) {
    throw new Error(response?.code ?? "unknown");
  }
}

export const historyRepository = {
  async getVersions(
    collection: HistoryCollection,
    recordId: string,
  ): Promise<HistoryTimeline> {
    const response = await pb.send("/api/history/versions", {
      method: "GET",
      query: { collection, recordId },
      requestKey: null,
    });

    assertSuccess(response);

    return {
      versions: response.versions ?? [],
      current: response.current ?? null,
      labels: response.labels ?? {},
    };
  },

  async getVersion(versionId: string): Promise<HistoryVersionDetail> {
    const response = await pb.send("/api/history/version", {
      method: "GET",
      query: { versionId },
      requestKey: null,
    });

    assertSuccess(response);

    // Les libellés sont renvoyés à côté de la version (ils décrivent d'autres enregistrements que
    // celui-ci) : on les rattache au détail, seul objet manipulé par l'interface.
    return { ...response.version, labels: response.labels ?? {} };
  },

  async getDeletedChildren(
    collection: HistoryCollection,
    recordId: string,
  ): Promise<DeletedRecord[]> {
    const response = await pb.send("/api/history/deleted", {
      method: "GET",
      query: { collection, recordId },
      requestKey: null,
    });

    assertSuccess(response);

    return response.deleted ?? [];
  },

  async restore(versionId: string): Promise<RestoreResult> {
    const response = await pb.send("/api/history/restore", {
      method: "POST",
      body: { versionId },
      requestKey: null,
    });

    assertSuccess(response);

    return {
      recordId: response.recordId,
      recreated: !!response.recreated,
      restoredFields: response.restoredFields ?? [],
      skippedFields: response.skippedFields ?? [],
      prunedFields: response.prunedFields ?? [],
      restoredChildren: response.restoredChildren ?? 0,
      failedChildren: response.failedChildren ?? 0,
    };
  },
};
